/**
 * SaleReviewModal — receipt-like preview shown BEFORE finalizing a sale that
 * contains any per-line discount, bill discount, or below-cost line.
 *
 * Gates (each only renders when actually needed):
 *   • Manager / Admin PIN — required when any line carries a discount or a
 *     sale-level (bill) discount exists. Resolves the `pos_discount` Pin
 *     Policy (configurable in Settings → Pin Policies).
 *   • Admin PIN — required when ANY line's NET unit price is at/below the
 *     line's stored capital. Resolves the `sell_below_capital` policy which,
 *     by platform default, excludes manager PINs and accepts only admin /
 *     owner PIN or TOTP.
 *
 * Layout mirrors a scrollable mini-receipt so the approver can review
 * exactly what's being sold and which lines are discounted before signing
 * off. The cashier may tap "Edit" to return to the cart.
 *
 * Browser password-save is suppressed via `autoComplete="one-time-code" data-lpignore="true"` and a
 * randomized field `name`, which prevents the "save password?" prompt that
 * was nagging cashiers on every approval.
 */
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { pinFieldProps } from '../../lib/pinField';
import { AlertTriangle, ShieldCheck, X, Edit3, Lock } from "lucide-react";
import { formatPHP } from "../../lib/utils";

export default function SaleReviewModal({
  open,
  onOpenChange,
  cart = [],
  subtotal = 0,
  billDiscountAmount = 0,
  grandTotal = 0,
  customer = null,
  paymentLabel = "",
  // Gating flags (derived in parent)
  needsDiscountPin = false,
  needsBelowCostPin = false,
  // Helpers from parent (keep math centralized to a single source of truth)
  computeNetUnit,
  computeLineTotal,
  isBelowCost,
  // Tendered (for cash sales) — purely informational on the review
  tenderedAmount = 0,
  changeAmount = 0,
  // Submission
  onApprove, // ({discountPin, belowCapitalPin}) => Promise
  onEdit, // () => void  — closes modal, returns to cart
}) {
  const [discountPin, setDiscountPin] = useState("");
  const [belowCapitalPin, setBelowCapitalPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Stable random name attribute. It was added to defeat Chrome's heuristic
  // password save and did NOT — the owner still got "Save password?" here on
  // 2026-09-23. Chrome offers because the field IS type="password"; the name
  // only affects matching. The real fix is pinFieldProps() below, which stops
  // declaring a PIN as a password at all. The nonce stays: two PIN boxes on one
  // screen still need distinct names.
  const fieldNonce = useMemo(
    () => `pin-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  const totalLineDiscounts = useMemo(() => {
    return cart.reduce((sum, c) => {
      const base = (c.price || 0) * (c.quantity || 0);
      const net = computeLineTotal ? computeLineTotal(c) : c.total || base;
      return sum + Math.max(0, base - net);
    }, 0);
  }, [cart, computeLineTotal]);

  const totalDiscount = totalLineDiscounts + billDiscountAmount;

  const reset = () => {
    setDiscountPin("");
    setBelowCapitalPin("");
    setError("");
    setSubmitting(false);
  };

  const handleApprove = async () => {
    setError("");
    if (needsDiscountPin && !discountPin) {
      setError("Manager / Admin PIN required for discounted sale.");
      return;
    }
    if (needsBelowCostPin && !belowCapitalPin) {
      setError("Admin PIN required for at/below-capital line.");
      return;
    }
    setSubmitting(true);
    try {
      await onApprove?.({
        discountPin: discountPin || undefined,
        belowCapitalPin: belowCapitalPin || undefined,
      });
      reset();
    } catch (e) {
      // Surface backend's detail (e.g., "Invalid PIN") if present.
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Approval failed. Try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange?.(v);
      }}
    >
      <DialogContent className="max-w-md mx-auto p-0 overflow-hidden" data-testid="sale-review-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle
            className="text-base font-bold flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
            data-testid="sale-review-title"
          >
            <ShieldCheck size={18} className="text-[#1A4D2E]" />
            Review &amp; Approve
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            This sale needs supervisor approval before finalizing.
          </DialogDescription>
        </DialogHeader>

        {/* Receipt body — scrollable */}
        <div
          className="max-h-[55vh] overflow-y-auto px-5 py-4 bg-white"
          data-testid="sale-review-body"
        >
          {/* Meta */}
          <div className="mb-3 text-[11px] text-slate-500 space-y-0.5">
            {customer?.name && (
              <div>
                <span className="text-slate-400">Customer:</span>{" "}
                <span className="font-medium text-slate-700">
                  {customer.name}
                </span>
              </div>
            )}
            {paymentLabel && (
              <div>
                <span className="text-slate-400">Payment:</span>{" "}
                <span className="font-medium text-slate-700">
                  {paymentLabel}
                </span>
              </div>
            )}
          </div>

          {/* Lines */}
          <div className="space-y-2.5">
            {cart.map((c) => {
              const dv = parseFloat(c.discount_value) || 0;
              const hasDisc = dv > 0;
              const netUnit = computeNetUnit ? computeNetUnit(c) : c.price;
              const lineTot = computeLineTotal ? computeLineTotal(c) : c.total;
              const belowCost = isBelowCost ? isBelowCost(c) : false;
              return (
                <div
                  key={c.product_id}
                  className={`border-b border-dashed border-slate-200 pb-2 ${
                    belowCost ? "bg-red-50/40 -mx-2 px-2 rounded" : ""
                  }`}
                  data-testid={`review-line-${c.product_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800 leading-snug flex-1 break-words">
                      {c.product_name}
                    </p>
                    <p
                      className={`text-sm font-bold tabular-nums font-mono whitespace-nowrap ${
                        belowCost ? "text-red-600" : "text-[#1A4D2E]"
                      }`}
                    >
                      {formatPHP(lineTot)}
                    </p>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono tabular-nums mt-0.5">
                    {c.quantity} ×{" "}
                    {hasDisc ? (
                      <>
                        <span className="line-through text-slate-400 mr-1">
                          {formatPHP(c.price)}
                        </span>
                        <span className="text-slate-700">
                          {formatPHP(netUnit)}
                        </span>
                      </>
                    ) : (
                      <span>{formatPHP(c.price)}</span>
                    )}
                  </div>
                  {hasDisc && (
                    <p
                      className="text-[10px] text-rose-600 font-medium mt-0.5"
                      data-testid={`review-line-discount-${c.product_id}`}
                    >
                      Discount:{" "}
                      {c.discount_type === "percent"
                        ? `${dv}% off`
                        : `${formatPHP(dv)}/unit`}{" "}
                      · saves{" "}
                      {formatPHP(
                        Math.max(0, c.price * c.quantity - lineTot)
                      )}
                    </p>
                  )}
                  {belowCost && (
                    <p
                      className="text-[10px] text-red-700 font-bold mt-0.5 flex items-center gap-1"
                      data-testid={`review-line-below-cost-${c.product_id}`}
                    >
                      <AlertTriangle size={11} /> Net price at/below capital
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Totals */}
          <div className="mt-4 pt-3 border-t-2 border-slate-200 space-y-1 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal</span>
              <span className="font-mono tabular-nums">
                {formatPHP(subtotal)}
              </span>
            </div>
            {totalLineDiscounts > 0 && (
              <div className="flex justify-between text-rose-600">
                <span>Line discounts</span>
                <span className="font-mono tabular-nums">
                  −{formatPHP(totalLineDiscounts)}
                </span>
              </div>
            )}
            {billDiscountAmount > 0 && (
              <div className="flex justify-between text-rose-600">
                <span>Bill discount</span>
                <span className="font-mono tabular-nums">
                  −{formatPHP(billDiscountAmount)}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-1.5 border-t border-slate-200 font-bold text-base">
              <span>Grand Total</span>
              <span
                className="font-mono tabular-nums text-[#1A4D2E]"
                data-testid="review-grand-total"
              >
                {formatPHP(grandTotal)}
              </span>
            </div>
            {tenderedAmount > 0 && (
              <>
                <div className="flex justify-between text-slate-500 text-[12px] pt-1">
                  <span>Tendered</span>
                  <span className="font-mono tabular-nums">
                    {formatPHP(tenderedAmount)}
                  </span>
                </div>
                {changeAmount > 0 && (
                  <div className="flex justify-between text-slate-500 text-[12px]">
                    <span>Change</span>
                    <span className="font-mono tabular-nums">
                      {formatPHP(changeAmount)}
                    </span>
                  </div>
                )}
              </>
            )}
            {totalDiscount > 0 && (
              <p className="text-[11px] text-slate-500 pt-1 italic">
                Total savings to customer: {formatPHP(totalDiscount)}
              </p>
            )}
          </div>
        </div>

        {/* Approval section */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 space-y-3">
          {needsDiscountPin && (
            <div data-testid="review-discount-pin-section">
              <label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1 mb-1">
                <Lock size={11} /> Manager / Admin PIN (discount approval)
              </label>
              <Input
                {...pinFieldProps({ name: `${fieldNonce}-d` })}
                value={discountPin}
                onChange={(e) => {
                  setDiscountPin(e.target.value);
                  setError("");
                }}
                placeholder="Enter PIN"
                className="h-11 text-lg font-mono text-center tracking-widest"
                data-testid="review-discount-pin-input"
                autoFocus
              />
            </div>
          )}
          {needsBelowCostPin && (
            <div data-testid="review-below-cost-pin-section">
              <label className="text-[11px] font-semibold text-red-700 flex items-center gap-1 mb-1">
                <AlertTriangle size={11} /> Admin PIN (at/below capital)
              </label>
              <Input
                {...pinFieldProps({ name: `${fieldNonce}-b` })}
                value={belowCapitalPin}
                onChange={(e) => {
                  setBelowCapitalPin(e.target.value);
                  setError("");
                }}
                placeholder="Admin PIN"
                className="h-11 text-lg font-mono text-center tracking-widest border-red-300 focus-visible:ring-red-300"
                data-testid="review-below-cost-pin-input"
                autoFocus={!needsDiscountPin}
              />
              <p className="text-[10px] text-red-600 mt-1">
                Manager PINs cannot approve at/below-capital sales by policy.
              </p>
            </div>
          )}

          {error && (
            <p
              className="text-[12px] text-red-600 font-medium"
              data-testid="review-error"
            >
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => {
                reset();
                onEdit?.();
              }}
              className="flex-1 h-11"
              data-testid="review-edit-btn"
              disabled={submitting}
            >
              <Edit3 size={14} className="mr-1.5" /> Edit
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting}
              className="flex-[1.5] h-11 bg-[#1A4D2E] hover:bg-[#15412a] text-white font-bold"
              data-testid="review-approve-btn"
            >
              {submitting ? "Approving..." : "Approve & Finalize"}
            </Button>
          </div>
        </div>

        {/* Top-right close */}
        <button
          onClick={() => {
            reset();
            onOpenChange?.(false);
          }}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 z-10"
          data-testid="review-close-btn"
          aria-label="Close review"
        >
          <X size={18} />
        </button>
      </DialogContent>
    </Dialog>
  );
}
